import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { api } from "api";
import { useFetchStateHelper, useFetchStateMerger } from "hooks/useFetchState";
import { DATE_FORMAT } from "constants/dateFormat";
import { useMemo } from "react";
import ChartsDataDto from "api/dtos/ChartsDataDto";
import { transformObjectKeys } from "utils/transformObjectKeys";

/**
 * @typedef {Object} UseChartDataProps
 * @property {string} fieldId - The ID of the field.
 * @property {string} [cropId] - The ID of the crop.
 * @property {boolean} [isCompareActive] - Whether comparison mode is active.
 * @property {string} startDate - The start date in ISO format.
 * @property {string} endDate - The end date in ISO format.
 * @property {Array} series - The series to fetch data for.
 * @property {boolean} isVisible - Whether the chart is visible.
 */

/**
 *
 * @param {UseChartDataProps} props
 * @returns {boolean}
 */
const isEnabled = (props) => {
  const { isVisible, fieldId, startDate, endDate, series } = props;
  return (
    isVisible &&
    !!fieldId &&
    !!startDate &&
    !!endDate &&
    Array.isArray(series) &&
    series.length > 0
  );
};

const STALE_TIME = 1000 * 60 * 2;

/**
 *
 * @param {UseChartDataProps} props
 */
const useChartWeatherData = (props) => {
  const { fieldId, startDate, endDate, series = [] } = props;

  const variables = useMemo(() => {
    const weatherVariables = [];
    series.forEach((series) => {
      const seriesKey = series.key;
      if (series.style === "area") {
        weatherVariables.push(`${seriesKey}Min`);
        weatherVariables.push(`${seriesKey}Mean`);
        weatherVariables.push(`${seriesKey}Max`);
      } else {
        weatherVariables.push(seriesKey);
      }
    });
    return weatherVariables;
  }, [series]);

  const { data, ...queryResponse } = useQuery({
    enabled: isEnabled(props),
    queryKey: ["weather", "historical", fieldId, startDate, endDate, variables],
    queryFn: () =>
      api.weather.fetchForDaysNew(fieldId, startDate, endDate, variables),
    // staleTime: STALE_TIME,
    initialData: {}, // set this in order to not be pending when idle
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    data,
    fetchState,
  };
};

/**
 *
 * @param {UseChartDataProps} props
 */
const useChartWeatherCumulativeData = (props) => {
  const { fieldId, cropId, startDate, endDate, series = [] } = props;

  const variables = useMemo(() => {
    const weatherVariables = [];
    series.forEach((series) => {
      const seriesKey = series.key;
      if (series.style === "area") {
        weatherVariables.push(`${seriesKey}Min`);
        weatherVariables.push(`${seriesKey}Mean`);
        weatherVariables.push(`${seriesKey}Max`);
      } else {
        weatherVariables.push(seriesKey);
      }
    });
    return weatherVariables;
  }, [series]);

  const { data, ...queryResponse } = useQuery({
    enabled: isEnabled(props) && !!cropId,
    queryKey: [
      "weather",
      "cumulative",
      fieldId,
      cropId,
      startDate,
      endDate,
      variables,
    ],
    queryFn: () =>
      api.weather.fetchCumulative({
        fieldId,
        cropId,
        startDate,
        endDate,
        variables,
      }),
    // staleTime: STALE_TIME,
    initialData: {}, // set this in order to not be pending when idle
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    data,
    fetchState,
  };
};

/**
 *
 * @param {UseChartDataProps} props
 */
const useChartIndicesData = (props) => {
  const { fieldId, startDate, endDate, series } = props;

  const indices = series?.map((s) => s.key);

  const { data, refetch, ...queryResponse } = useQuery({
    enabled: isEnabled(props),
    queryKey: ["indices-stats", fieldId, startDate, endDate, indices],
    queryFn: async () => {
      return await api.satellite.fetchStatistics({
        fieldId,
        startDate,
        endDate,
        indices,
      });
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "processing" ? 5000 : false;
    },
    initialData: {}, // set this in order to not be pending when idle
    refetchIntervalInBackground: true,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    data: data?.data,
    fetchState,
    isRefetching: data?.status === "processing",
  };
};

/**
 *
 * @param {UseChartDataProps} props
 * @returns
 */
export const useChartData = (props) => {
  const {
    startDate: propsStartDate,
    endDate: propsEndDate,
    series,
    isCompareActive,
  } = props;

  const startDate = moment(propsStartDate).format(DATE_FORMAT);
  const endDate = moment(propsEndDate).format(DATE_FORMAT);

  const selectedSeries = series?.filter((s) => s.isSelected);
  const weatherSeries = selectedSeries?.filter(
    (s) => s.endpoint === "weather" && s.category === "weather"
  );
  const cumulativeSeries = selectedSeries?.filter(
    (s) => s.endpoint === "weather" && s.category === "cumulative"
  );

  const indicesSeries = selectedSeries?.filter(
    (s) => s.endpoint === "satellite"
  );

  const { data: weatherData = {}, fetchState: weatherFetchState } =
    useChartWeatherData({
      ...props,
      series: weatherSeries,
      startDate,
      endDate,
    });

  const {
    data: indicesData = {},
    isRefetching,
    fetchState: indicesFetchState,
  } = useChartIndicesData({
    ...props,
    series: indicesSeries,
    startDate,
    endDate,
  });

  const { data: cumulativeData = {}, fetchState: cumulativeFetchState } =
    useChartWeatherCumulativeData({
      ...props,
      series: cumulativeSeries,
      startDate,
      endDate,
    });

  const allData = useMemo(() => {
    const data = {};

    const allDates = populateDates(startDate, endDate);

    allDates.forEach((date) => {
      data[date] = {
        ...weatherData[date],
        ...cumulativeData[date],
        ...indicesData[date],
      };
    });

    // Apply min-max grouping for area series
    const areaSeries = weatherSeries
      .filter((s) => s.style === "area")
      .map((s) => s.key);

    const finalData = ChartsDataDto.fromApi(data, areaSeries);

    const applyComparisonData = isCompareActive
      ? finalData.map((d) =>
          transformObjectKeys(d, (key) =>
            key !== "date" ? key + "_compare" : key
          )
        )
      : finalData;

    return applyComparisonData;
  }, [
    startDate,
    endDate,
    weatherSeries,
    weatherData,
    cumulativeData,
    indicesData,
    isCompareActive,
  ]);

  const { fetchState } = useFetchStateMerger(
    weatherFetchState,
    indicesFetchState,
    cumulativeFetchState
  );

  return {
    data: allData,
    fetchState,
    isRefetching,
  };
};

const populateDates = (startDate, endDate) => {
  const dates = [];
  let current = moment(startDate);
  const last = moment(endDate);
  while (current.isSameOrBefore(last)) {
    dates.push(current.format(DATE_FORMAT));
    current.add(1, "day");
  }

  return dates;
};
